/*
 * REBROWN 물방울 엔진
 * 화면 전체에 투명한 캔버스를 한 장 깔고, 페이지 안의 [data-droplet] 요소 자리마다 비눗방울을 그린다.
 * 가까운 방울끼리는 자연스럽게 합쳐지고, 마우스를 따라오는 방울이 지나가면 끌려가 늘어났다가 떨어진다.
 *
 * 요소에 쓸 수 있는 속성
 *   data-droplet            방울 표시 (요소의 가로 폭 = 방울 지름)
 *   data-img / data-alpha   방울 속에 보일 사진 주소와 보이는 정도(0~1, 기본 1)
 *   data-img2 / data-alpha2 두 번째 사진 (사진이 바뀔 때 겹쳐서 부드럽게 교체)
 *   data-vis                방울 크기 배율 (0이면 숨김) — 스크롤 연출에서 사용
 *   data-free               "1"이면 커서 방울에 끌려가지 않음
 */
(() => {
  'use strict';

  const conf = Object.assign({
    cursor: true,   // 마우스를 따라오는 방울
    size: 70,       // 커서 방울 반지름(px)
    follow: .09,    // 따라오는 속도
    bounce: .78,    // 쫀득함(감쇠)
    wobble: 1,      // 테두리 일렁임
    iri: 1,         // 무지갯빛 세기
    loader: true,   // 첫 방문 로딩 연출
  }, window.RB_DROPLETS || {});

  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = matchMedia('(pointer: fine)').matches;
  const designMode = !!(window.Shopify && window.Shopify.designMode);
  const loaderEl = document.getElementById('rb-loader');

  const canvas = document.createElement('canvas');
  canvas.className = 'rb-droplets';
  canvas.setAttribute('aria-hidden', 'true');
  document.body.appendChild(canvas);
  const gl = canvas.getContext('webgl', { alpha: true, premultipliedAlpha: true, antialias: false });

  const hideLoader = () => { if (loaderEl) loaderEl.classList.add('is-done'); document.documentElement.classList.add('rb-loaded'); };
  if (!gl) { canvas.remove(); hideLoader(); return; } // WebGL이 없으면 방울 없이 페이지만

  // ---------------------------------------------------------------- 셰이더
  const VS = 'attribute vec2 a; void main(){ gl_Position = vec4(a, 0., 1.); }';
  const FS = `
  precision highp float;
  uniform vec2 uRes;
  uniform vec4 uD[24];   // 방울별 (x, y, 반지름, 출렁임)  0: 커서, 1-3: 꼬리, 4-17: 페이지 방울, 18-23: 튀는 방울
  uniform vec4 uI[14];   // 페이지 방울별 사진 (칸 번호 A, 보임 A, 칸 번호 B, 보임 B)
  uniform float uK, uTime, uDpr, uWob, uIri;
  uniform sampler2D uAtlas; // 사진 모음 (2048px, 4×4칸)

  float smin(float a, float b, float k){
    float h = clamp(.5 + .5*(b-a)/k, 0., 1.);
    return mix(b, a, h) - k*h*(1.-h);
  }
  // 테두리가 천천히 일렁이고, 움직일수록 더 말랑거림
  float blob(vec2 p, vec4 D, float fi){
    vec2 v = p - D.xy;
    float r = D.z, d0 = length(v) - r;
    if (abs(d0) > r * .25 || uWob < .001) return d0;
    float a = atan(v.y, v.x + 1e-5);
    float k = sin(3.*a + uTime*1.1 + fi*1.7) * .6 + sin(5.*a - uTime*.8 + fi) * .4;
    return length(v) - r * (1. + (.016 + .07*D.w) * uWob * k);
  }
  float sdf(vec2 p){
    float d = 1e5;
    for (int i=0; i<24; i++) {
      if (uD[i].z < .5) continue;
      float b = blob(p, uD[i], float(i));
      d = d > 9e4 ? b : smin(d, b, min(uK, uD[i].z*1.2));
    }
    return d;
  }
  // 이 픽셀을 덮는 방울의 크기 → 방울마다 자기 크기 기준으로 볼록한 모양
  float localR(vec2 p){
    float sw = 0., sr = 0.;
    for (int i=0; i<24; i++) {
      float ri = uD[i].z;
      if (ri < .5) continue;
      float di = length(p - uD[i].xy) - ri;
      float w = exp(-max(di, 0.) / (ri*.35 + 2.)) * ri;
      sw += w; sr += w*ri;
    }
    return sw > 0. ? sr/sw : 40.;
  }
  float energyAt(vec2 p){
    float e = 0.;
    for (int i=0; i<24; i++) {
      if (uD[i].z < .5) continue;
      e = max(e, uD[i].w * (1. - smoothstep(uD[i].z*.6, uD[i].z*1.8, length(p - uD[i].xy))));
    }
    return e;
  }
  float height(vec2 p, float Rl){
    float r = clamp(1. + sdf(p)/Rl, 0., 1.);
    return sqrt(1. - r*r);
  }
  vec4 tileAt(float tile, vec2 uv){
    float tx = mod(tile, 4.), ty = floor(tile / 4.);
    return texture2D(uAtlas, vec2((tx + uv.x) / 4., 1. - (ty + 1.) / 4. + uv.y / 4.));
  }
  // 방울 속 사진: 흐릿하고 크게 → 선명하게, 가장자리는 렌즈처럼 살짝 부풂
  vec4 photos(vec2 p, vec3 n){
    vec4 acc = vec4(0.);
    for (int i=4; i<18; i++) {
      vec4 I = uI[i-4];
      if (uD[i].z < .5 || (I.y < .001 && I.w < .001)) continue;
      vec2 q = (p - uD[i].xy) / uD[i].z;
      float l = length(q);
      if (l > 1.) continue;
      float m = 1. - smoothstep(.62, .94, l);
      if (I.x >= 0. && I.y > .001) {
        vec2 uv = clamp(.5 + q*.5*mix(1.35, 1.04, I.y) + n.xy*.05, .003, .997);
        vec4 c = tileAt(I.x, uv) * m * I.y;
        acc = c + acc*(1. - c.a);
      }
      if (I.z >= 0. && I.w > .001) {
        vec2 uv = clamp(.5 + q*.5*mix(1.35, 1.04, I.w) + n.xy*.05, .003, .997);
        vec4 c = tileAt(I.z, uv) * m * I.w;
        acc = c + acc*(1. - c.a);
      }
    }
    return acc;
  }
  vec4 over(vec3 col, float a, vec4 under){ return vec4(col*a, a) + under*(1. - a); }

  void main(){
    vec2 p = gl_FragCoord.xy;
    float d = sdf(p);
    if (d > 1.5*uDpr) { gl_FragColor = vec4(0.); return; }

    float Rl = max(localR(p), 4.*uDpr), e = uDpr;
    vec2 grad = vec2(height(p+vec2(e,0.), Rl) - height(p-vec2(e,0.), Rl),
                     height(p+vec2(0.,e), Rl) - height(p-vec2(0.,e), Rl)) / (2.*e);
    vec3 n = normalize(vec3(-grad*Rl, 1.));
    vec2 nd = n.xy / max(length(n.xy), 1e-4);
    float rim = pow(1. - n.z, 2.2);
    vec3 film = .5 + .5*cos(6.2831*(vec3(0., .33, .67) + rim*1.6 + atan(n.y, n.x + 1e-5)*.22 + uTime*.05));

    vec4 o = photos(p, n);
    // 아주 옅은 몸통 → 흰 배경 위에서도 방울이 읽힘
    o = over(vec3(.94, .95, .98), .05, o);
    // 테두리 윤곽: 회보랏빛 + 박막 색 (밝은 배경에서 형태를 잡아줌)
    o = over(mix(vec3(.5, .52, .6), film*.85, .45*uIri), pow(1. - n.z, 6.)*.34, o);
    // 밝게 빛나는 테두리 + 무지갯빛 막
    float ra = rim*.32*(.55 + .45*smoothstep(-.2, .8, dot(nd, normalize(vec2(-.6, .8)))));
    o = over(mix(vec3(1.), film, .55*uIri), ra, o);
    // 둥근 표면에 휘어 비친 스튜디오 조명 창 (앞면 + 반대편 작은 창)
    vec2 rv = 2.*n.z*n.xy;
    vec2 w1 = (rv - vec2(-.5, .6)) / vec2(.3, .2);
    vec2 w2 = (rv - vec2(.62, -.55)) / vec2(.16, .1);
    float win = (1. - smoothstep(.7, 1., pow(pow(abs(w1.x), 4.) + pow(abs(w1.y), 4.), .25))) * .6
              + (1. - smoothstep(.6, 1., pow(pow(abs(w2.x), 4.) + pow(abs(w2.y), 4.), .25))) * .3;
    o = over(vec3(1.), clamp(win * smoothstep(.05, .35, length(n.xy)), 0., 1.), o);
    // 반대편 안쪽에 모인 초승달 광채
    o = over(vec3(1.), smoothstep(.5, .95, length(n.xy)) * pow(max(dot(nd, normalize(vec2(.55, -.8))), 0.), 2.) * .3, o);
    // 작고 날카로운 반짝임
    vec3 L = normalize(vec3(-.45, .55, .7));
    float s1 = max(reflect(-L, n).z, 0.);
    o = over(vec3(1.), clamp(pow(s1, 260.)*.9 + pow(s1, 1200.)*1.4, 0., 1.), o);
    // 움직일 때만 살짝 도는 무지갯빛
    o.rgb = clamp(o.rgb + (film - .5) * energyAt(p) * .16 * uIri * o.a, 0., 1.);

    gl_FragColor = o * clamp(-d / (1.2*uDpr), 0., 1.);
  }`;

  const compile = (type, src) => {
    const s = gl.createShader(type);
    gl.shaderSource(s, src); gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s));
    return s;
  };
  let prog;
  try {
    prog = gl.createProgram();
    gl.attachShader(prog, compile(gl.VERTEX_SHADER, VS));
    gl.attachShader(prog, compile(gl.FRAGMENT_SHADER, FS));
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(prog));
  } catch (err) {
    console.warn('[REBROWN] 물방울 효과를 끌게요:', err); canvas.remove(); hideLoader(); return;
  }
  gl.useProgram(prog);
  document.documentElement.classList.add('rb-gl'); // 물방울이 사진을 그리므로 원래 사진은 숨김 (.rb-gl-hide)
  gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
  const aLoc = gl.getAttribLocation(prog, 'a');
  gl.enableVertexAttribArray(aLoc); gl.vertexAttribPointer(aLoc, 2, gl.FLOAT, false, 0, 0);
  const U = n => gl.getUniformLocation(prog, n);
  const u = { res: U('uRes'), d: U('uD'), i: U('uI'), k: U('uK'), time: U('uTime'), dpr: U('uDpr'), wob: U('uWob'), iri: U('uIri'), atlas: U('uAtlas') };
  gl.enable(gl.BLEND); gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

  // ---------------------------------------------------------------- 사진 모음(아틀라스)
  const ATLAS = 2048, TILE = 512, TILES = 16;
  const atlasTex = gl.createTexture();
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, atlasTex);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, ATLAS, ATLAS, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  const tileCanvas = document.createElement('canvas');
  tileCanvas.width = tileCanvas.height = TILE;
  const tiles = new Map(); // 주소 → { tile, ready, last }
  let tick = 0;

  function tileFor(url) {
    if (!url) return -1;
    let t = tiles.get(url);
    if (!t) {
      const used = new Set([...tiles.values()].map(v => v.tile));
      let tile = [...Array(TILES).keys()].find(k => !used.has(k));
      if (tile === undefined) { // 가장 오래 안 쓴 사진 자리를 재사용
        let oldUrl = null, oldest = Infinity;
        tiles.forEach((v, k) => { if (v.last < oldest) { oldest = v.last; oldUrl = k; } });
        tile = tiles.get(oldUrl).tile; tiles.delete(oldUrl);
      }
      t = { tile, ready: false, last: tick };
      tiles.set(url, t);
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const c = tileCanvas.getContext('2d'), s = Math.min(TILE / img.width, TILE / img.height) * .92;
        c.clearRect(0, 0, TILE, TILE);
        c.drawImage(img, (TILE - img.width * s) / 2, (TILE - img.height * s) / 2, img.width * s, img.height * s);
        try {
          gl.bindTexture(gl.TEXTURE_2D, atlasTex);
          gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
          gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
          gl.texSubImage2D(gl.TEXTURE_2D, 0, (tile % 4) * TILE, ATLAS - (Math.floor(tile / 4) + 1) * TILE, gl.RGBA, gl.UNSIGNED_BYTE, tileCanvas);
          gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
          t.ready = true;
        } catch (err) { console.warn('[REBROWN] 사진을 방울에 넣지 못했어요:', url, err); }
      };
      img.src = url;
    }
    t.last = tick;
    return t.ready ? t.tile : -1;
  }

  // ---------------------------------------------------------------- 상태
  let W = 0, H = 0, DPR = 1;
  function resize() {
    DPR = Math.min(window.devicePixelRatio || 1, 1.75);
    W = innerWidth; H = innerHeight;
    canvas.width = Math.round(W * DPR); canvas.height = Math.round(H * DPR);
    gl.viewport(0, 0, canvas.width, canvas.height);
  }
  addEventListener('resize', resize);
  resize();

  const D = new Float32Array(96), I = new Float32Array(56);
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const smooth = x => x * x * x * (x * (x * 6 - 15) + 10);
  const useCursor = conf.cursor && finePointer && !reduceMotion;

  const ptr = { x: W * .5, y: H * .5, seen: false, last: 0 };
  addEventListener('pointermove', e => {
    if (e.pointerType !== 'mouse') return;
    ptr.x = e.clientX; ptr.y = e.clientY; ptr.seen = true; ptr.last = performance.now();
  }, { passive: true });
  addEventListener('pointerdown', () => { cur.wobV -= .09; }, { passive: true });
  // 물방울을 누르면 톡 눌렸다가 출렁 (터치 기기에서는 출렁인 뒤에 링크로 이동)
  const jiggle = el => {
    el.classList.remove('is-jiggle'); void el.offsetWidth; el.classList.add('is-jiggle');
    const st = states.get(el);
    if (st) { st.popV -= .13; st.vx += (Math.random() - .5) * 16; st.vy -= 7; }
  };
  addEventListener('pointerdown', e => {
    const el = e.target.closest && e.target.closest('[data-droplet]');
    if (el && !reduceMotion) jiggle(el);
  }, { passive: true });
  addEventListener('click', e => {
    const el = e.target.closest && e.target.closest('a[data-droplet]');
    if (!el || finePointer || reduceMotion || e.defaultPrevented || e.metaKey || e.ctrlKey) return;
    e.preventDefault();
    setTimeout(() => { location.href = el.href; }, 420);
  });
  document.addEventListener('mouseleave', () => { ptr.seen = false; });

  const cur = { x: W * .5, y: H * .5, vx: 0, vy: 0, wob: 0, wobV: 0, e: 0 };
  const trail = [0, 1, 2].map(() => ({ x: W * .5, y: H * .5 }));
  const sheds = Array.from({ length: 6 }, () => ({ x: 0, y: 0, vx: 0, vy: 0, r0: 0, life: 0 }));
  let shedIdx = 0, shedTimer = 0;
  const states = new WeakMap();

  // 페이지의 방울 요소 목록 (테마 편집기에서 섹션을 바꿔도 따라오도록 주기적으로 다시 찾음)
  let anchors = [], lastScan = 0;
  const scan = () => { anchors = [...document.querySelectorAll('[data-droplet]')]; lastScan = performance.now(); };

  // 로딩: 가운데 뭉친 방울들이 돌다가 100%가 되면 톡톡 튀며 회전하듯 흩어짐
  const showLoader = conf.loader && loaderEl && !designMode && !reduceMotion;
  const pctEl = loaderEl && loaderEl.querySelector('[data-pct]');
  const virt = [74, 50, 42, 34, 26, 20].map((r, i) => ({ r, ang: i * 1.05, ox: 0, oy: 0, vx: 0, vy: 0, s: 1, sv: 0 }));
  const t0 = performance.now();
  let burstAt = showLoader ? Infinity : t0, burstDone = !showLoader, docReady = document.readyState === 'complete';
  addEventListener('load', () => { docReady = true; });
  if (!showLoader) hideLoader();

  const feel = (st, target, dt) => {
    target = clamp(target, 0, 1);
    st.e += (target - st.e) * (1 - Math.pow(target > st.e ? .75 : .96, dt));
  };

  // ---------------------------------------------------------------- 프레임
  let last = performance.now();
  function frame(now) {
    const dt = Math.min((now - last) / 16.667, 3); last = now; tick++;
    const t = now / 1000;
    if (now - lastScan > 700) scan();
    D.fill(0); I.fill(0);
    for (let k = 0; k < 14; k++) { I[k * 4] = -1; I[k * 4 + 2] = -1; }
    const put = (slot, x, y, r, e) => { D.set([x * DPR, (H - y) * DPR, Math.max(0, r) * DPR, e], slot * 4); };

    // ----- 로딩 진행
    const since = now - t0;
    if (showLoader && burstAt === Infinity) {
      const p = Math.min(since / 1500, docReady ? 1 : .96);
      if (pctEl) pctEl.textContent = Math.floor(p * 100) + '%';
      if (p >= 1) burstAt = now + 260;
    }
    const loading = now < burstAt + 1300;
    if (!burstDone && now >= burstAt) {
      burstDone = true; hideLoader();
      sheds.forEach((s, k) => {
        const a = k / sheds.length * Math.PI * 2 + .4, sp = 9 + Math.random() * 6;
        s.x = W * .5 + Math.cos(a) * 40; s.y = H * .5 + Math.sin(a) * 40;
        s.vx = Math.cos(a) * sp - Math.sin(a) * sp * .6; s.vy = Math.sin(a) * sp + Math.cos(a) * sp * .6;
        s.r0 = 6 + Math.random() * 10; s.life = 1;
      });
      virt.forEach((v, k) => { // 접선 방향으로 차서 나선을 그리며 날아가고 톡 사라짐
        const a = v.ang, sp = 14 + k * 3;
        v.vx = -Math.sin(a) * sp + Math.cos(a) * sp * .7; v.vy = Math.cos(a) * sp + Math.sin(a) * sp * .7; v.sv = -.02;
      });
    }
    // 페이지 방울은 로딩이 끝나면 톡 하고 나타남
    const pageVis = showLoader ? smooth(clamp((now - burstAt - 250) / 700, 0, 1)) : 1;

    // ----- 커서 방울
    let slot0R = 0;
    if (useCursor && now >= burstAt) {
      // 마우스가 화면에 있을 때만 보이고, 들어오면 톡 나타나고 나가면 작아지며 사라짐
      cur.show = (cur.show || 0) + ((ptr.seen ? 1 : 0) - (cur.show || 0)) * (1 - Math.pow(.88, dt));
      if (ptr.seen && cur.show < .05) { cur.x = ptr.x; cur.y = ptr.y; cur.vx = cur.vy = 0; trail.forEach(b => { b.x = ptr.x; b.y = ptr.y; }); }
      const tx = ptr.x, ty = ptr.y;
      const damp = Math.pow(conf.bounce, dt);
      cur.vx = (cur.vx + (tx - cur.x) * conf.follow * dt) * damp;
      cur.vy = (cur.vy + (ty - cur.y) * conf.follow * dt) * damp;
      cur.x += cur.vx * dt; cur.y += cur.vy * dt;
      cur.wobV = (cur.wobV - cur.wob * .18 * dt) * Math.pow(.86, dt); cur.wob += cur.wobV * dt;
      const speed = Math.hypot(cur.vx, cur.vy);
      feel(cur, speed / 18 + Math.abs(cur.wobV) * 10, dt);
      slot0R = conf.size * (1 + .012 * Math.sin(t * 1.4) + cur.wob) * cur.show * (showLoader ? smooth(clamp((now - burstAt - 300) / 500, 0, 1)) : 1);
      put(0, cur.x, cur.y, slot0R, cur.e);
      let prev = cur;
      trail.forEach((b, k) => {
        const kk = 1 - Math.pow(1 - [.34, .26, .2][k], dt);
        b.x += (prev.x - b.x) * kk; b.y += (prev.y - b.y) * kk; prev = b;
        put(k + 1, b.x, b.y, slot0R * [.6, .42, .28][k], cur.e);
      });
      shedTimer -= dt;
      if (speed > 22 && shedTimer <= 0) { // 빠르게 움직이면 뒤에서 작은 방울이 떨어져 나감
        const s = sheds[shedIdx++ % sheds.length], nx = -cur.vx / speed, ny = -cur.vy / speed, side = (Math.random() - .5) * .9;
        s.x = cur.x + (nx - ny * side) * slot0R * .95; s.y = cur.y + (ny + nx * side) * slot0R * .95;
        s.vx = cur.vx * .18 - ny * side * 3; s.vy = cur.vy * .18 + nx * side * 3;
        s.r0 = slot0R * (.1 + Math.random() * .12); s.life = 1; shedTimer = 5;
      }
    }

    // ----- 로딩 방울 (페이지 방울 자리를 잠깐 빌려 씀)
    let slot = 4;
    if (showLoader && loading) {
      const p = burstAt === Infinity ? Math.min(since / 1500, 1) : 1;
      const squeeze = now >= burstAt - 260 && now < burstAt ? 1 - smooth(clamp((now - burstAt + 260) / 260, 0, 1)) * .8 : 1;
      virt.forEach(v => {
        if (now < burstAt) {
          v.ang += dt * (.03 + .24 * p * p);
          v.ox = Math.cos(v.ang) * v.r * .2 * (1 + .7 * p) * squeeze;
          v.oy = Math.sin(v.ang) * v.r * .2 * (1 + .7 * p) * squeeze;
        } else {
          v.ox += v.vx * dt; v.oy += v.vy * dt; v.vx *= Math.pow(.93, dt); v.vy *= Math.pow(.93, dt);
          v.sv = (v.sv + (v.s > .55 ? -.012 : -.03) * dt) * Math.pow(.92, dt); v.s = Math.max(0, v.s + v.sv * dt);
        }
        put(slot++, W * .5 + v.ox, H * .5 + v.oy, v.r * v.s, now >= burstAt ? .8 : .3 * p);
      });
    }

    // ----- 페이지 방울
    const pad = 200;
    for (const el of anchors) {
      if (slot >= 18) break;
      const rc = el.getBoundingClientRect();
      if (rc.width < 2 || rc.bottom < -pad || rc.top > H + pad || rc.right < -pad || rc.left > W + pad) continue;
      const vis = parseFloat(el.dataset.vis ?? '1') * pageVis;
      if (!(vis > .01)) continue;
      let st = states.get(el);
      if (!st) { st = { ox: 0, oy: 0, vx: 0, vy: 0, e: 0, px: null, py: null, pop: 1, popV: 0, seen: false }; states.set(el, st); }
      const r = rc.width / 2 * vis;
      const hx = rc.left + rc.width / 2, hy = rc.top + rc.height / 2;
      // 처음 화면에 나타날 때 톡
      if (!st.seen && pageVis > .5) { st.seen = true; st.popV = -.08; }
      // 커서 방울이 가까이 오면 끌려가 늘어나고, 멀어지면 출렁이며 제자리로
      let tx = 0, ty = 0;
      if (slot0R > 0 && el.dataset.free !== '1') {
        const dx = cur.x - hx, dy = cur.y - hy, dist = Math.hypot(dx, dy), reach = (slot0R + r) * 1.4;
        if (dist < reach) { const pull = 1 - dist / reach; tx = dx * pull * .45; ty = dy * pull * .45; }
      }
      st.vx = (st.vx + (tx - st.ox) * .08 * dt) * Math.pow(.86, dt);
      st.vy = (st.vy + (ty - st.oy) * .08 * dt) * Math.pow(.86, dt);
      st.ox += st.vx * dt; st.oy += st.vy * dt;
      st.popV = (st.popV + (1 - st.pop) * .22 * dt) * Math.pow(.8, dt); st.pop += st.popV * dt;
      const move = st.px === null ? 0 : Math.hypot(hx - st.px, hy - st.py) / dt;
      st.px = hx; st.py = hy;
      feel(st, Math.hypot(st.vx, st.vy) / 3 + Math.hypot(st.ox, st.oy) / 80 + move / 30, dt);
      put(slot, hx + st.ox, hy + st.oy, r * st.pop, st.e);
      // 방울 속 사진
      const k = (slot - 4) * 4;
      if (el.dataset.img) { I[k] = tileFor(el.dataset.img); I[k + 1] = clamp(parseFloat(el.dataset.alpha ?? '1'), 0, 1) * pageVis; }
      if (el.dataset.img2) { I[k + 2] = tileFor(el.dataset.img2); I[k + 3] = clamp(parseFloat(el.dataset.alpha2 ?? '1'), 0, 1) * pageVis; }
      slot++;
    }

    // ----- 튀는 작은 방울
    sheds.forEach((s, k) => {
      if (s.life <= 0) return;
      s.x += s.vx * dt; s.y += s.vy * dt;
      s.vx *= Math.pow(.9, dt); s.vy *= Math.pow(.9, dt);
      s.life -= .008 * dt;
      put(18 + k, s.x, s.y, s.r0 * Math.sqrt(clamp(s.life / .35, 0, 1)), Math.max(0, s.life) * .8);
    });

    gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT);
    gl.uniform2f(u.res, canvas.width, canvas.height);
    gl.uniform4fv(u.d, D); gl.uniform4fv(u.i, I);
    gl.uniform1f(u.k, 60 * DPR); gl.uniform1f(u.time, t); gl.uniform1f(u.dpr, DPR);
    gl.uniform1f(u.wob, reduceMotion ? 0 : conf.wobble); gl.uniform1f(u.iri, conf.iri);
    gl.uniform1i(u.atlas, 0);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    requestAnimationFrame(frame);
  }
  scan();
  requestAnimationFrame(frame);
})();
